import qs from 'query-string'

import { MESSAGING_ENDPOINTS } from './endpoints'
import type {
  ApiError,
  ExtensionSummary,
  MessageGroup,
  MessageGroupListParams,
  MessageGroupListResult,
  MessageGroupMember,
  MessageGroupWritePayload,
  MessagingApi,
  MessagingApiClient,
  PaginatedApiResult,
  PhoneNumberDetail,
} from './messagingApi.types'

/**
 * Extract a human-readable error message from a DRF-style API response.
 *
 * DRF validation errors come back as `{ errors: [{ code, detail, attr }] }`
 * (the `drf-standardized-errors` format), so the top-level `.message`
 * field is usually undefined on 400 responses. Without inspecting the
 * structured `errors` array callers fall through to a generic "Failed to
 * …" string and the user never sees meaningful validation feedback such
 * as "A phone number cannot be both primary and secondary."
 */
const extractApiErrorMessage = (
  data: ApiError | undefined,
  fallback: string,
): string => {
  if (!data) return fallback
  if (data.errors && data.errors.length > 0) {
    // Normalize each detail so multiple messages join cleanly regardless
    // of whether the backend already terminates them with punctuation.
    // Without this `.join('. ')` would produce `"First problem.. Second
    // problem."` for already-punctuated DRF output, and `.join(' ')`
    // would run unpunctuated details together as a single sentence.
    const details = data.errors
      .map((e) => e.detail)
      .filter((d): d is string => !!d)
      .map((d) => d.trim().replace(/[.!?]+$/, ''))
      .filter((d) => d.length > 0)
    if (details.length > 0) return `${details.join('. ')}.`
  }
  return data.message ?? fallback
}

/**
 * Create a real messaging API from an HTTP client adapter.
 *
 * Group CRUD, phone-number listing, and member management hit real backend endpoints.
 * Member read endpoints may return 404 until backend is ready; we return [] in that case.
 */
export const createMessagingApi = (
  client: MessagingApiClient,
): MessagingApi => ({
  // -----------------------------------------------------------------------
  // Message group CRUD  (real API)
  // -----------------------------------------------------------------------

  async getMessageGroups(
    pbxId: number,
    params?: MessageGroupListParams,
  ): Promise<MessageGroupListResult> {
    const queryString = qs.stringify(
      {
        pbx_id: pbxId,
        limit: params?.limit,
        offset: params?.offset,
        ordering: params?.ordering,
        search: params?.search,
      },
      { skipNull: true, skipEmptyString: true },
    )
    const url = queryString
      ? `${MESSAGING_ENDPOINTS.MESSAGE_GROUPS_LIST}?${queryString}`
      : MESSAGING_ENDPOINTS.MESSAGE_GROUPS_LIST

    const response = await client.get<PaginatedApiResult<MessageGroup>>(url)

    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to fetch message groups (${response.status ?? 'unknown'})`,
        ),
      )
    }

    return response.data as MessageGroupListResult
  },

  async getMessageGroup(groupId: number): Promise<MessageGroup> {
    const response = await client.get<MessageGroup>(
      MESSAGING_ENDPOINTS.MESSAGE_GROUP_DETAIL(groupId),
    )

    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to fetch message group ${groupId} (${response.status ?? 'unknown'})`,
        ),
      )
    }

    return response.data as MessageGroup
  },

  async createMessageGroup(
    payload: MessageGroupWritePayload,
  ): Promise<MessageGroup> {
    const response = await client.post<MessageGroup>(
      MESSAGING_ENDPOINTS.MESSAGE_GROUPS_LIST,
      payload,
    )

    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to create message group (${response.status ?? 'unknown'})`,
        ),
      )
    }

    return response.data as MessageGroup
  },

  async updateMessageGroup(
    groupId: number,
    payload: Partial<MessageGroupWritePayload>,
  ): Promise<MessageGroup> {
    const response = await client.patch<MessageGroup>(
      MESSAGING_ENDPOINTS.MESSAGE_GROUP_DETAIL(groupId),
      payload,
    )

    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to update message group ${groupId} (${response.status ?? 'unknown'})`,
        ),
      )
    }

    return response.data as MessageGroup
  },

  async deleteMessageGroup(groupId: number): Promise<void> {
    const response = await client.delete<undefined>(
      MESSAGING_ENDPOINTS.MESSAGE_GROUP_DETAIL(groupId),
    )

    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to delete message group ${groupId} (${response.status ?? 'unknown'})`,
        ),
      )
    }
  },

  // -----------------------------------------------------------------------
  // Phone numbers  (real API)
  // -----------------------------------------------------------------------

  async getAvailableSmsNumbers(pbxId: number): Promise<PhoneNumberDetail[]> {
    const allResults: PhoneNumberDetail[] = []
    let offset = 0
    const limit = 250

    // Paginate through all phone numbers for this PBX
    while (true) {
      const queryString = qs.stringify(
        { pbx_id: pbxId, limit, offset },
        { skipNull: true, skipEmptyString: true },
      )
      const url = `${MESSAGING_ENDPOINTS.PHONE_NUMBERS_LIST}?${queryString}`

      const response =
        await client.get<PaginatedApiResult<PhoneNumberDetail>>(url)

      if (!response.ok) {
        throw new Error(
          extractApiErrorMessage(
            response.data as ApiError | undefined,
            `Failed to fetch phone numbers (${response.status ?? 'unknown'})`,
          ),
        )
      }

      allResults.push(...response.data!.results)

      if (!response.data!.next) break
      offset += limit
    }

    // Match the legacy filters: sms_activated_at IS NOT NULL,
    // sms_relay = 0, local_presence = 0, not already in a group
    return allResults.filter(
      (p) =>
        p.sms_activated_at &&
        !p.sms_information?.relay &&
        !p.sms_information?.group_name &&
        (!p.local_presence || p.local_presence === 0),
    )
  },

  // -----------------------------------------------------------------------
  // Member management  (real API; 404 → [] for reads until endpoints exist)
  // -----------------------------------------------------------------------

  async getMessageGroupMembers(groupId: number): Promise<MessageGroupMember[]> {
    // `limit=1000` bypasses DRF's default page size (25), which otherwise
    // silently truncated the list — a group with 40 members only showed 25,
    // since we take `results` from a single (first) page with no pagination
    // loop. 1000 matches the "fetch all" pattern getExtensions uses below and
    // is well above any realistic group size.
    const queryString = qs.stringify(
      { group_id: groupId, limit: 1000 },
      { skipNull: true, skipEmptyString: true },
    )
    const url = `${MESSAGING_ENDPOINTS.MESSAGE_GROUP_MEMBERS}?${queryString}`

    const response = await client.get<
      PaginatedApiResult<MessageGroupMember> | MessageGroupMember[]
    >(url)

    if (response.status === 404) return []
    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to fetch message group members (${response.status ?? 'unknown'})`,
        ),
      )
    }

    const data = response.data!
    return Array.isArray(data) ? data : data.results
  },

  async getExtensions(pbxId: number): Promise<ExtensionSummary[]> {
    // The "Available Members" panel only shows lines, so we scope to
    // `type=ExtensionLine` and `status_filter=active` server-side. The
    // type filter is required for `status_filter` to engage at all:
    // `mango-api`'s `ExtensionFilter.filter_status` short-circuits and
    // ignores the param unless `?type=ExtensionLine` is also present
    // (see `_is_line_type_filter`), which is why the previous version
    // sent `status_filter=active` alone and inactive lines still
    // slipped through.
    //
    // The page re-checks `active !== false` defensively in case a
    // backend revision ever stops honoring the filter combination.
    //
    // `limit=1000` bypasses DRF's default page size (10), which
    // otherwise silently truncated the list — a PBX with 11 active
    // lines would only show 10 available members and the 11th was
    // unaddable. 1000 matches the "fetch all" pattern the apps use
    // against `/extensions/` and is well above any realistic PBX size;
    // the legacy admin's `listExtensionsByPbx` returns every row in
    // one query.
    const queryString = qs.stringify(
      {
        pbx_id: pbxId,
        type: 'ExtensionLine',
        status_filter: 'active',
        limit: 1000,
      },
      { skipNull: true, skipEmptyString: true },
    )
    const url = `${MESSAGING_ENDPOINTS.EXTENSIONS}?${queryString}`

    const response = await client.get<
      PaginatedApiResult<ExtensionSummary> | ExtensionSummary[]
    >(url)

    if (response.status === 404) return []
    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to fetch extensions (${response.status ?? 'unknown'})`,
        ),
      )
    }

    const data = response.data!
    return Array.isArray(data) ? data : data.results
  },

  async addMessageGroupMember(
    groupId: number,
    extensionId: number,
  ): Promise<MessageGroupMember> {
    const response = await client.post<MessageGroupMember>(
      MESSAGING_ENDPOINTS.MESSAGE_GROUP_MEMBERS,
      { group_id: groupId, extension_id: extensionId },
    )

    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to add member (${response.status ?? 'unknown'})`,
        ),
      )
    }

    return response.data as MessageGroupMember
  },

  async removeMessageGroupMember(memberId: number): Promise<void> {
    const response = await client.delete<undefined>(
      MESSAGING_ENDPOINTS.MESSAGE_GROUP_MEMBER_DETAIL(memberId),
    )

    if (!response.ok) {
      throw new Error(
        extractApiErrorMessage(
          response.data as ApiError | undefined,
          `Failed to remove member (${response.status ?? 'unknown'})`,
        ),
      )
    }
  },
})
